'use client'

import { Button } from '@/core/components/form/button/button'
import { LuExpand } from 'react-icons/lu'

import SafeImage from '@/features/veluvit/components/ui/media-image'
import { cn } from '@/core/utils/common'

import ProfileModal from '@/features/veluvit/components/chat/sheets/profile-modal'
import DialogueHead from '@/features/veluvit/components/chat/dialogue-head'
import { speechInner } from '@/features/veluvit/components/reader/reader-blocks'
import { RichText } from '@/features/veluvit/components/ui/rich-text'
import { useChatSpeaker } from '@/features/veluvit/hooks/use-chat-speaker'
import { openModal } from '@/features/veluvit/stores/overlay.store'
import {
  findEngineCommand,
  parseDirective,
  type Directive,
} from '@/features/veluvit/lib/engine-commands'
import { narrationLines } from '@/features/veluvit/lib/narration-lines'

/* 대화 한 줄의 기본 모양 — 레퍼런스 msgHTML 의 img · sit · sys · char · user.
   variant 'preview' 는 상세 화면 도입부 미리보기(원본 app.css), 'chat' 은 대화방(v2-chat.css 덮어쓰기)이다. */

type Variant = 'preview' | 'chat'

const SPLIT_OPEN = '('

/** 엔진의 별표 행동 표기와 예전에 저장한 앞머리 (행동)을 표시한다. 대사 안의 괄호는 건드리지 않는다 (레퍼런스 userMessageParts) */
export function userMessageParts(value: string): {
  act?: string
  text: string
} {
  const original = String(value ?? '')
  const source = original.trimStart()
  const action = source.match(/^(\*\*|\*)([^*]+)\1(?=\s|$)/)
  if (action?.[2].trim())
    return {
      act: action[2].trim(),
      text: source.slice(action[0].length).trimStart(),
    }
  if (source[0] !== SPLIT_OPEN) return { text: original }
  let depth = 0
  for (let i = 0; i < source.length; i++) {
    if (source[i] === '(') depth++
    else if (source[i] === ')' && --depth === 0) {
      if (source[i + 1] && !/\s/.test(source[i + 1])) break
      const act = source.slice(1, i).trim()
      if (act) return { act, text: source.slice(i + 1).trimStart() }
      break
    }
  }
  return { text: original }
}

/* 대화방 문장 규칙 — 지문은 말풍선 없이 본문 글씨로, 말은 카톡식(2026-10-07):
   - 캐릭터: 왼쪽 둥근 프사 + 프사 오른쪽 위 이름, 그 아래로 회색 말풍선(버튼 보조색 bg-surface-raised). 말풍선은 프사 폭만큼 들여 쓴다
   - 나: 오른쪽 흰 말풍선(bg-foreground) + 검정 글씨
   예전 코랄 말풍선 · 어두운 글래스 말풍선 · 기울인 이름표(퀘스트 연출)는 걷었다. */

/** 캐릭터 말풍선 들여쓰기 = 프사(40) + 간격(8) — CharHead 와 CharLine 이 같은 값을 쓴다 */
const AVATAR_INDENT = 'ml-12'
const CHAR_BUBBLE =
  'rounded-panel rounded-tl-control bg-surface-raised px-3.5 py-2.5 text-c14h leading-[1.65] whitespace-pre-wrap text-t1 [overflow-wrap:anywhere] over10:text-c15'

export function SitLine({
  text,
  variant,
  className,
}: {
  text: string
  variant: Variant
  className?: string
}) {
  return (
    <div
      className={cn(
        'my-0.5 max-w-full px-0.5 text-c14h leading-[1.7] text-t2',
        variant === 'chat'
          ? // 비주얼 노벨 내레이션 — 가운데 · 기울임 · 위아래 가는 선과 옅은 글래스 띠. 조사가 다음 줄로 떨어지지 않게 어절 단위로 고르게 줄바꿈
            'w-full max-w-[62ch] self-center border-y border-foreground/10 bg-[linear-gradient(90deg,transparent,rgb(255_255_255/0.05)_20%,rgb(255_255_255/0.05)_80%,transparent)] px-3 py-2 text-center leading-[1.8] text-balance break-keep whitespace-pre-wrap italic [overflow-wrap:anywhere] over10:text-c15'
          : 'text-left text-pretty',
        className
      )}
      data-type={variant === 'chat' ? '' : undefined}
    >
      {variant === 'chat' ? (
        narrationLines(text).map((line, i) => (
          <span key={i} className="block">
            <RichText text={line} />
          </span>
        ))
      ) : (
        <RichText text={text} />
      )}
    </div>
  )
}

export function SysLine({
  text,
  className,
}: {
  text: React.ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'max-w-[94%] self-center rounded-field bg-gem/8 px-3 py-1.5 text-center text-c12 text-gem shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--sub)_20%,transparent)]',
        className
      )}
    >
      {text}
    </div>
  )
}

/** 둥근 프사 — 등록된 출연 캐릭터의 프사 → 대표 이미지. 사진 없는 주변 인물만 이름 한 글자. */
/** className 에 위치(relative · absolute)를 함께 넘긴다 — cn 은 겹치는 클래스를 지우지 않아 여기서 relative 를 깔면 absolute 와 둘 다 붙어 프사가 제자리로 돌아온다(2026-10-13 이름 밀림 원인) */
export function CharAvatar({
  workId,
  speaker,
  speakerId,
  speakerType,
  className = 'relative size-8',
}: {
  workId: string
  speaker?: string | null
  speakerId?: string | null
  speakerType?: string
  className?: string
}) {
  const profile = useChatSpeaker(workId, { speaker, speakerId, speakerType })
  if (profile.avatar) {
    // 누르면 프로필 크게 보기(카카오톡 프로필 결) — 2026-10-13
    return (
      <Button
        type="button"
        aria-label={`${profile.name} 프로필 보기`}
        onClick={() =>
          openModal(<ProfileModal workId={workId} profile={profile} />, {
            label: '프로필',
          })
        }
        className={cn(
          'flex-none cursor-pointer overflow-hidden rounded-full bg-surface transition-[filter] hover:brightness-110 focus-visible:brightness-110',
          className
        )}
        variant="bare"
      >
        <SafeImage
          src={profile.avatar}
          fallbackSrc={profile.image}
          alt=""
          fill
          sizes="40px"
          className="object-cover object-[50%_12%]"
          appearance="plain"
        />
      </Button>
    )
  }
  return (
    <div
      className={cn(
        'grid flex-none place-items-center overflow-hidden rounded-full bg-surface-strong text-c13 font-bold text-t1',
        className
      )}
    >
      {profile.name.charAt(profile.name.length > 2 ? 1 : 0)}
    </div>
  )
}

/**
 * 대화방 답장 머리(카톡식) — 답장 묶음 첫머리에 한 번: 왼쪽 둥근 프사 + 그 오른쪽 위 이름. speaker null = 작품 주인공.
 * overlap — 바로 아래가 캐릭터 말풍선이면 프사가 첫 말풍선 옆까지 내려온다(카톡처럼 프사 · 이름 · 첫 말풍선이 한 덩어리).
 * 아래가 지문 등 다른 줄이면 프사가 그 줄에 겹치지 않게 프사 높이만큼 자리를 차지한다.
 */
export function CharHead({
  workId,
  speaker,
  speakerId,
  speakerType,
  overlap = false,
}: {
  workId: string
  speaker?: string | null
  speakerId?: string | null
  speakerType?: string
  overlap?: boolean
}) {
  const profile = useChatSpeaker(workId, { speaker, speakerId, speakerType })
  return (
    <DialogueHead
      overlap={overlap}
      name={profile.name}
      avatar={
        <CharAvatar
          workId={workId}
          speaker={speaker}
          speakerId={speakerId}
          speakerType={speakerType}
          className="relative block size-10"
        />
      }
    />
  )
}

/** 카탈로그·세션 없이 쓰는 실제 채팅 말풍선. 스튜디오도 같은 원본을 쓴다. */
export function CharBubble({ text }: { text: string }) {
  return (
    <div
      className={cn(
        'w-fit max-w-[min(calc(100%-3rem),56ch)]',
        AVATAR_INDENT,
        CHAR_BUBBLE
      )}
      data-type=""
    >
      <RichText text={speechInner(text)} />
    </div>
  )
}

export function CharLine({
  workId,
  text,
  speaker,
  variant,
}: {
  workId: string
  text: string
  speaker?: string | null
  variant: Variant
}) {
  const profile = useChatSpeaker(workId, { speaker })
  // 말풍선이 곧 대사라 모델이 양끝에 단 따옴표(" “ ”)는 뗀다 — 리더가 다시 감쌀 때 “"…"” 로 겹치던 것과 같은 원인(2026-10-13)
  const line = speechInner(text)
  // 대화방은 말하는 사람을 CharHead 가 묶음 첫머리에 한 번 보이므로 말풍선만 — 프사 폭만큼 들여 써서 이름 아래 줄을 맞춘다
  if (variant === 'chat') {
    return <CharBubble text={text} />
  }
  return (
    <div className="flex max-w-full items-start gap-2">
      <CharAvatar workId={workId} speaker={speaker} />
      <div className="min-w-0 flex-[1_1_auto]">
        <div className="mb-1 text-c12 text-t3">{profile.name}</div>
        <div className={cn('w-fit max-w-full', CHAR_BUBBLE)}>
          <RichText text={line} />
        </div>
      </div>
    </div>
  )
}

/**
 * 엔진 명령어(/점프 · /사건 …)로 보낸 턴 — 말풍선 대신 오른쪽에 연출 지시 표(감독 큐 카드)로 보인다.
 * 내가 한 말이 아니라 장면에 내린 지시라는 걸 모양으로 가른다
 */
function DirectiveLine({ directive }: { directive: Directive }) {
  const command = findEngineCommand(directive.key)
  return (
    <div className="flex max-w-[86%] items-center gap-2 self-end rounded-full bg-foreground/[0.06] py-1.5 pr-3.5 pl-1.5 shadow-[inset_0_0_0_1px_rgb(255_255_255/0.1)] backdrop-blur-md">
      <span className="inline-flex h-6 flex-none -skew-x-12 items-center rounded-control bg-sub px-2">
        <b className="block skew-x-12 text-c13 leading-none font-bold text-gem-ink">
          /{directive.key}
        </b>
      </span>
      <span className="min-w-0 truncate text-c13 text-t2">
        {directive.arg || command?.desc}
      </span>
    </div>
  )
}

export function UserLine({
  text,
  variant,
  allowDirective = true,
}: {
  text: string
  variant: Variant
  allowDirective?: boolean
}) {
  const directive =
    variant === 'chat' && allowDirective ? parseDirective(text) : null
  if (directive) return <DirectiveLine directive={directive} />
  const parts = userMessageParts(text)
  return (
    <div
      className={cn(
        'max-w-[94%] self-end',
        variant === 'chat' &&
          'max-w-[86%] whitespace-pre-wrap [overflow-wrap:anywhere]'
      )}
    >
      {/* 내 말 — 흰 말풍선 + 검정 글씨(코랄 채움은 걷었다). 한글은 띄어쓰기 단위로 넘긴다(break-keep — “어쩔티/비”처럼 낱말 중간에서 끊지 않게).
          overflow-wrap 은 anywhere 가 아니라 break-word — anywhere 는 말풍선 최소 폭을 글자 한 자로 줄여 좁은 줄에서 낱말이 갈렸다. 줄보다 긴 낱말만 쪼갠다.
          (행동) 머리는 같은 말풍선 안에 *행동* 과 같은 결(기울임 · 옅게)로 먼저 쓴다 — 말풍선 위에 따로 띄우니 누구 말인지 흐려졌다(2026-10-13): “결과 화면을 가리키다 이번 판은 인정.” */}
      <div className="ml-auto w-fit max-w-full rounded-panel rounded-tr-control bg-foreground px-3.5 py-2.5 text-c14h leading-[1.6] font-medium break-keep text-background [overflow-wrap:break-word] over10:text-c15">
        {parts.act && (
          <em className="font-normal italic opacity-70">
            {parts.act}
            {parts.text ? ' ' : ''}
          </em>
        )}
        {parts.text && <RichText text={parts.text} />}
      </div>
    </div>
  )
}

/** 장면 컷. 미리보기는 4:5 로 자르고, 대화방은 원본 비율 그대로 전체보기 버튼을 얹는다 */
export function SceneImage({
  src,
  fallback,
  variant,
  onOpen,
}: {
  src: string
  fallback?: string
  variant: Variant
  onOpen?: () => void
}) {
  if (variant === 'preview') {
    return (
      <div className="relative -mx-3.5 aspect-[4/5]">
        <SafeImage
          src={src}
          fallbackSrc={fallback}
          alt=""
          fill
          sizes="(max-width: 480px) 100vw, 480px"
          className="object-cover object-[50%_16%]"
          appearance="plain"
        />
      </div>
    )
  }
  // 본문 여백 안의 가로 카드로 자른다(크랙 참고) — 세로 원본을 폭 가득 펼치면 한 화면이 몸통으로 차고 얼굴이 잘렸다.
  // 얼굴이 보통 위쪽 1/5 에 있어 그 지점을 남기고, 전체는 눌러서 본다
  return (
    // PC 는 칸이 넓어 폭 가득이면 한 장이 화면을 덮는다 — 560px 까지만
    <div className="my-1 w-full self-stretch over10:max-w-140">
      <Button
        type="button"
        aria-label="대화 장면 전체보기"
        onClick={onOpen}
        className="group relative block aspect-[4/3] w-full cursor-zoom-in overflow-hidden rounded-panel bg-foreground/[0.04] text-left text-t1"
        variant="bare"
      >
        <SafeImage
          src={src}
          fallbackSrc={fallback}
          alt="대화 장면"
          fill
          sizes="(min-width: 1001px) 760px, 100vw"
          // 내 스냅(/generated/snap-*)은 로그인 쿠키가 있어야 받는다 — 최적화 서버는 쿠키를 넘기지 않아 브라우저가 직접 받게 둔다
          unoptimized={src.includes('/generated/snap-')}
          className="object-cover object-[50%_18%]"
          appearance="plain"
        />
        <span className="pointer-events-none absolute right-2.5 bottom-2.5 grid size-8 place-items-center rounded-full bg-black/45 text-white/90 backdrop-blur-md opacity-80 transition-opacity group-hover:opacity-100">
          <LuExpand aria-hidden="true" className="size-3.5" />
        </span>
      </Button>
    </div>
  )
}
